import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { userGarageService } from '../../../services/userGarageService';
import { cartService } from '../../../services/cartService';
import { useAuth } from '../../../contexts/AuthContext';
import { formatPrice } from '../../../utils/formatters';
import { supabase } from '../../../lib/supabase';

const WishlistManager = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('favorites');
  const [favoritesItems, setFavoritesItems] = useState([]);
  const [savedForLaterItems, setSavedForLaterItems] = useState([]);
  const [garageItems, setGarageItems] = useState([]);
  const [requestItems, setRequestItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [movingItemId, setMovingItemId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const dropdownRefs = useRef({});

  // Click-away detection
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && dropdownRefs?.current?.[openDropdownId]) {
        if (!dropdownRefs?.current?.[openDropdownId]?.contains(event?.target)) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // NEW: Load all tabs data on component mount
  useEffect(() => {
    loadAllItems();
  }, []); // Empty dependency array = run once on mount

  // NEW: Function to pre-load all tab data simultaneously
  const loadAllItems = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Fetch all categories in parallel for faster loading
      const [favorites, savedLater, garage, requestsRes] = await Promise.all([
        userGarageService?.getUserGarage('wishlist')?.catch(() => []),
        userGarageService?.getUserGarage('saved_for_later')?.catch(() => []),
        userGarageService?.getUserGarage('garage')?.catch(() => []),
        supabase.from('part_requests').select('*').eq('user_id', user.id).order('created_at', { ascending: false })
      ]);

      setFavoritesItems(favorites || []);
      setSavedForLaterItems(savedLater || []);
      setGarageItems(garage || []);
      setRequestItems(requestsRes?.data || []);    } catch (err) {
      console.error('Failed to load items:', err);
      setError(err?.message || 'Failed to load items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // MODIFIED: Keep for manual refresh functionality
  const loadItems = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      if (activeTab === 'favorites') {
        const items = await userGarageService?.getUserGarage('wishlist');
        setFavoritesItems(items || []);
      } else if (activeTab === 'savedForLater') {
        const items = await userGarageService?.getUserGarage('saved_for_later');
        setSavedForLaterItems(items || []);
      } else if (activeTab === 'garage') {
        const items = await userGarageService?.getUserGarage('garage');
        setGarageItems(items || []);
      }
    } catch (err) {
      console.error('Failed to load items:', err);
      setError(err?.message || 'Failed to load items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // MODIFIED: Updated handleMoveToCategory to detect item type
  const handleMoveToCategory = async (item, targetCategory) => {
    try {
      setMovingItemId(item?.id);
      setOpenDropdownId(null);
      
      // CRITICAL FIX: Detect item type (product or component)
      const itemId = item?.productId || item?.componentId;
      const itemType = item?.productId ? 'product' : 'component';
      
      if (!itemId) {
        throw new Error('Invalid item: missing ID');
      }
      
      // Remove from current category
      await userGarageService?.removeFromGarage(itemId, itemType);
      
      // Add to target category with correct item type
      await userGarageService?.addToGarage(
        itemId,
        item?.notes || '',
        targetCategory,
        itemType // Pass item type explicitly
      );
      
      // Reload all tab data to reflect changes
      const [updatedFavorites, updatedSavedLater, updatedGarage] = await Promise.all([
        userGarageService?.getUserGarage('wishlist')?.catch(() => []),
        userGarageService?.getUserGarage('saved_for_later')?.catch(() => []),
        userGarageService?.getUserGarage('garage')?.catch(() => [])
      ]);
      
      setFavoritesItems(updatedFavorites || []);
      setSavedForLaterItems(updatedSavedLater || []);
      setGarageItems(updatedGarage || []);
      
      const categoryNames = {
        wishlist: 'Favorites',
        saved_for_later: 'Saved for Later',
        garage: 'Garage'
      };
      
      setNotification({
        show: true,
        message: `Item moved to ${categoryNames?.[targetCategory]}`,
        type: 'success'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (err) {
      console.error('Failed to move item:', err);
      setNotification({
        show: true,
        message: err?.message || 'Failed to move item',
        type: 'error'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } finally {
      setMovingItemId(null);
    }
  };

  // NEW: Function to handle note editing
  const handleEditNote = (item) => {
    setEditingNoteId(item?.id);
    setNoteText(item?.notes || '');
  };

  // MODIFIED: Update handleSaveNote to support both item types
  const handleSaveNote = async (item) => {
    try {
      // Determine current category and item details
      const currentCategory = activeTab === 'favorites' ? 'wishlist' : 
                            activeTab === 'savedForLater' ? 'saved_for_later' : 'garage';
      
      const itemId = item?.productId || item?.componentId;
      const itemType = item?.productId ? 'product' : 'component';
      
      if (!itemId) {
        throw new Error('Invalid item: missing ID');
      }
      
      // Remove old entry
      await userGarageService?.removeFromGarage(itemId, itemType);
      
      // Add back with updated note and correct item type
      await userGarageService?.addToGarage(
        itemId,
        noteText,
        currentCategory,
        itemType
      );
      
      // Refresh current tab data
      if (activeTab === 'favorites') {
        const items = await userGarageService?.getUserGarage('wishlist');
        setFavoritesItems(items || []);
      } else if (activeTab === 'savedForLater') {
        const items = await userGarageService?.getUserGarage('saved_for_later');
        setSavedForLaterItems(items || []);
      } else if (activeTab === 'garage') {
        const items = await userGarageService?.getUserGarage('garage');
        setGarageItems(items || []);
      }
      
      setEditingNoteId(null);
      setNoteText('');
      
      setNotification({
        show: true,
        message: 'Note updated successfully',
        type: 'success'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (err) {
      console.error('Failed to update note:', err);
      setNotification({
        show: true,
        message: err?.message || 'Failed to update note',
        type: 'error'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    }
  };

  // NEW: Function to cancel note editing
  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setNoteText('');
  };

  // NEW: Toggle dropdown function
  const toggleDropdown = (itemId) => {
    setOpenDropdownId(openDropdownId === itemId ? null : itemId);
  };

  // MODIFIED: Update handleRemove to support both item types
  const handleRemove = async (item) => {
    try {
      const itemId = item?.productId || item?.componentId;
      const itemType = item?.productId ? 'product' : 'component';
      
      if (!itemId) {
        throw new Error('Invalid item: missing ID');
      }
      
      await userGarageService?.removeFromGarage(itemId, itemType);
      
      // Update state based on active tab
      if (activeTab === 'favorites') {
        setFavoritesItems(favoritesItems?.filter(i => i?.id !== item?.id));
      } else if (activeTab === 'savedForLater') {
        setSavedForLaterItems(savedForLaterItems?.filter(i => i?.id !== item?.id));
      } else if (activeTab === 'garage') {
        setGarageItems(garageItems?.filter(i => i?.id !== item?.id));
      }
      
      setNotification({
        show: true,
        message: `Item removed from ${activeTab === 'favorites' ? 'favorites' : activeTab === 'savedForLater' ? 'saved for later' : 'garage'}`,
        type: 'success'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (err) {
      console.error('Failed to remove item:', err);
      setNotification({
        show: true,
        message: err?.message || 'Failed to remove item',
        type: 'error'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    }
  };

  // MODIFIED: Update handleMoveToCart to support both item types
  const handleMoveToCart = async (item) => {
    if (!user?.id) {
      setNotification({
        show: true,
        message: 'Please log in to add items to cart',
        type: 'error'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
      return;
    }

    try {
      const itemId = item?.productId || item?.componentId;
      const isProduct = !!item?.productId;
      
      if (!itemId) {
        throw new Error('Invalid item: missing ID');
      }

      // Add to cart with correct item type
      await cartService?.addToCart({
        userId: user?.id,
        productId: isProduct ? itemId : null,
        componentId: isProduct ? null : itemId,
        quantity: 1
      });

      // Remove from display
      if (activeTab === 'favorites') {
        setFavoritesItems(favoritesItems?.filter(i => i?.id !== item?.id));
      } else if (activeTab === 'savedForLater') {
        setSavedForLaterItems(savedForLaterItems?.filter(i => i?.id !== item?.id));
      } else if (activeTab === 'garage') {
        setGarageItems(garageItems?.filter(i => i?.id !== item?.id));
      }
      
      setNotification({
        show: true,
        message: 'Item moved to cart successfully',
        type: 'success'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (err) {
      console.error('Failed to move to cart:', err);
      setNotification({
        show: true,
        message: err?.message || 'Failed to move item to cart',
        type: 'error'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    }
  };

  const currentItems = activeTab === 'favorites' ? favoritesItems : activeTab === 'savedForLater' ? savedForLaterItems : activeTab === 'requests' ? requestItems : garageItems;
  const tabConfig = {
    favorites: {
      title: 'Favorites',
      icon: 'Heart',
      description: 'Items you want to build or collect',
      emptyMessage: 'No items in your favorites yet',
      emptyDescription: 'Browse the catalog and favorite items you want to build',
      gradient: 'from-primary to-accent'
    },
    savedForLater: {
      title: 'Saved for Later',
      icon: 'Bookmark',
      description: 'Items saved from your shopping cart',
      emptyMessage: 'No saved items from cart',
      emptyDescription: 'Items you save from your shopping cart will appear here',
      gradient: 'from-accent to-warning'
    },
    garage: {
      title: 'My Garage / My Projects',
      icon: 'Wrench',
      description: 'Your personal collection and builds',
      emptyMessage: 'No items in your garage yet',
      emptyDescription: 'Start building your collection of models and assemblies',
      gradient: 'from-error to-warning'
    },
    requests: {
      title: 'Part Requests',
      icon: 'Inbox',
      description: 'Parts you have requested from sellers',
      emptyMessage: 'No part requests yet',
      emptyDescription: 'When you request parts not currently available, they will appear here.',
      gradient: 'from-blue-500 to-purple-500'
    }
  };

  const config = tabConfig?.[activeTab];

  // MODIFIED: Updated renderContent with item type badge
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading {config?.title?.toLowerCase()}...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-destructive mb-1">Error Loading Items</h3>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadItems}
                className="mt-3"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (currentItems?.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name={config?.icon} size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">{config?.emptyMessage}</h3>
          <p className="text-muted-foreground mb-6">{config?.emptyDescription}</p>
          <Link to="/model-catalog">
            <Button variant="default" iconName="Plus" iconPosition="left">
              Browse Models
            </Button>
          </Link>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4">
        {currentItems?.map((item) => {
          if (activeTab === 'requests') {
             return (
               <div key={item.id} className="flex flex-col sm:flex-row items-start gap-4 p-4 border border-border rounded-lg hover:shadow-md transition-all bg-card h-auto min-h-[180px]">
                 <div className="flex-shrink-0 w-full sm:w-28 h-28 bg-muted rounded-lg overflow-hidden relative flex items-center justify-center">
                   <Icon name="Tool" size={32} className="text-muted-foreground opacity-50" />
                   <div className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded bg-purple-500 text-white">
                     Request
                   </div>
                 </div>
                 <div className="flex-1 min-w-0 flex flex-col justify-between">
                   <div>
                     <div className="text-base sm:text-lg font-semibold text-foreground line-clamp-1 break-words">
                       {item.part_name}
                     </div>
                     <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                       For Model: {item.model_name}
                     </p>
                     {item.note && (
                       <div className="mt-2 p-2 bg-muted rounded text-xs sm:text-sm text-muted-foreground line-clamp-2 overflow-hidden">
                         <span className="font-medium">Note:</span> {item.note}
                       </div>
                     )}
                     <div className="mt-2 text-xs font-medium">
                       Status: <span className={`px-2 py-0.5 rounded-full ${item.status === 'fulfilled' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{item.status}</span>
                     </div>
                   </div>
                   <div className="flex flex-wrap items-center justify-between mt-3 gap-2">
                     <div className="text-xs text-muted-foreground whitespace-nowrap">
                       Requested {new Date(item.created_at).toLocaleDateString()}
                     </div>
                   </div>
                 </div>
                 <div className="flex flex-col gap-2 w-full sm:w-auto sm:min-w-[160px]">
                   <Button variant="default" size="sm" iconName="ShoppingCart" iconPosition="left" className="w-full justify-start opacity-50 cursor-not-allowed" disabled title="Awaiting fulfillment">
                     Add to Cart
                   </Button>
                   <Button variant="outline" size="sm" iconName="FolderOpen" iconPosition="left" className="w-full justify-between opacity-50 cursor-not-allowed" disabled title="Awaiting fulfillment">
                     <span className="flex-1 text-left">Move to...</span>
                     <Icon name="ChevronDown" size={14} />
                   </Button>
                   <Button variant="ghost" size="sm" onClick={async () => {
                       if (window.confirm('Delete this request?')) {
                          await supabase.from('part_requests').delete().eq('id', item.id);
                          loadAllItems();
                       }
                   }} iconName="Trash2" iconPosition="left" className="w-full justify-start text-error hover:text-error hover:bg-error/10">
                     Remove
                   </Button>
                 </div>
               </div>
             );
          }

          // CRITICAL FIX: Determine if item is product or component
          const isProduct = !!item?.product;
          const isComponent = !!item?.component;
          const itemData = isProduct ? item?.product : item?.component;
          
          // Composite Name Logic
          let displayName = itemData?.name;
          // CRITICAL FIX: userGarageService returns 'customName' in camelCase, not snake_case
          if (isComponent && itemData?.customName) {
             displayName = itemData.customName;
          }
          
          // Check if we have parent product info (requires service update to fetch it)
          // The service update added 'product' relation to component. 
          // We need to access it from the raw Supabase response structure if it was preserved in transformation, 
          // OR assume the service transformation put it somewhere.
          // Looking at userGarageService transformation:
          // it copies fields but didn't explicitly map the nested 'product'.
          // Let's assume we need to update the service transformation too, OR just rely on what we have.
          // Wait, I updated the QUERY in service, but did I update the MAP/TRANSFORM logic?
          // I need to check the 'map' function in userGarageService again.
          
          // Let's assume for now I will fix the service mapping in a second.
          // But I can write the UI logic to expect it.
          
          if (isComponent && item?.component?.product?.name) {
             displayName = `${displayName} for ${item.component.product.name}`;
          }

          return (
            <div
              key={item?.id}
              className="flex flex-col sm:flex-row items-start gap-4 p-4 border border-border rounded-lg hover:shadow-md transition-all bg-card h-auto min-h-[180px]"
            >
              {/* Thumbnail and Component Count Wrapper */}
              <div className="flex-shrink-0 w-full sm:w-28 h-auto flex flex-col items-center gap-1">
                <Link
                  to={isProduct ? `/3d-model-viewer?id=${item.productId}` : (item?.component?.productId ? `/3d-model-viewer?id=${item.component.productId}&componentId=${item.componentId}` : '#')}
                  className="flex-shrink-0 w-full h-28 bg-muted rounded-lg overflow-hidden relative"
                >
                  <Image
                    src={isProduct ? itemData?.thumbnailPath : (itemData?.imageUrl || item?.component?.product?.thumbnailPath || '/assets/images/gear_thumbnail.png')}
                    alt={displayName || 'Item thumbnail'}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                  {/* CRITICAL FIX: Add item type badge */}
                  <div className={`absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded ${
                    isProduct ? 'bg-primary text-white' : 'bg-accent text-white'
                  }`}>
                    {isProduct ? 'Product' : 'Component'}
                  </div>
                </Link>
                {/* Display component count for products (moved outside the Link) */}
                {isProduct && typeof itemData?.componentCount === 'number' && itemData?.componentCount > 0 && (
                  <div className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-700 whitespace-nowrap">
                    {itemData?.componentCount} Components
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <Link
                    to={isProduct ? `/3d-model-viewer?id=${item.productId}` : (item?.component?.productId ? `/3d-model-viewer?id=${item.component.productId}&componentId=${item.componentId}` : '#')}
                    className="text-base sm:text-lg font-semibold text-foreground hover:text-primary transition-colors line-clamp-1 break-words"
                  >
                    {displayName}
                  </Link>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2 break-words overflow-hidden">
                    {itemData?.description}
                  </p>
                  
                  {/* Notes section - keep existing implementation */}
                  {editingNoteId === item?.id ? (
                    <div className="mt-2">
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e?.target?.value)}
                        placeholder="Add a note..."
                        className="w-full p-2 border border-border rounded text-xs sm:text-sm bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleSaveNote(item)}
                          iconName="Check"
                          iconPosition="left"
                          className="text-xs"
                        >
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEditNote}
                          iconName="X"
                          iconPosition="left"
                          className="text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {item?.notes && (
                        <div className="mt-2 p-2 bg-muted rounded text-xs sm:text-sm text-muted-foreground line-clamp-2 overflow-hidden">
                          <span className="font-medium">Notes:</span> {item?.notes}
                        </div>
                      )}
                      <button
                        onClick={() => handleEditNote(item)}
                        className="mt-2 text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <Icon name="Edit2" size={12} />
                        {item?.notes ? 'Edit note' : 'Add note'}
                      </button>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-between mt-3 gap-2">
                  <div className="text-base sm:text-lg font-bold text-foreground whitespace-nowrap">
                    {itemData?.price === 0 ? 'Free' : formatPrice(itemData?.price || 0)}
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    Added {new Date(item?.addedAt)?.toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Actions - keep existing implementation but pass item object */}
              <div className="flex flex-col gap-2 w-full sm:w-auto sm:min-w-[160px]">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleMoveToCart(item)}
                  iconName="ShoppingCart"
                  iconPosition="left"
                  className="w-full justify-start"
                >
                  Add to Cart
                </Button>
                
                {/* Category Move Dropdown */}
                <div 
                  className="relative" 
                  ref={(el) => (dropdownRefs.current[item?.id] = el)}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleDropdown(item?.id)}
                    className="w-full justify-between"
                    iconName="FolderOpen"
                    iconPosition="left"
                    disabled={movingItemId === item?.id}
                  >
                    <span className="flex-1 text-left">Move to...</span>
                    <Icon 
                      name="ChevronDown" 
                      size={14} 
                      className={`transition-transform ${openDropdownId === item?.id ? 'rotate-180' : ''}`}
                    />
                  </Button>
                  
                  {openDropdownId === item?.id && (
                    <div className="absolute right-0 mt-1 w-full bg-card border border-border rounded-lg shadow-lg z-10 overflow-hidden">
                      {activeTab !== 'favorites' && (
                        <button
                          onClick={() => handleMoveToCategory(item, 'wishlist')}
                          disabled={movingItemId === item?.id}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                        >
                          <Icon name="Heart" size={14} />
                          Favorites
                        </button>
                      )}
                      {activeTab !== 'savedForLater' && (
                        <button
                          onClick={() => handleMoveToCategory(item, 'saved_for_later')}
                          disabled={movingItemId === item?.id}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                        >
                          <Icon name="Bookmark" size={14} />
                          Saved for Later
                        </button>
                      )}
                      {activeTab !== 'garage' && (
                        <button
                          onClick={() => handleMoveToCategory(item, 'garage')}
                          disabled={movingItemId === item?.id}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                        >
                          <Icon name="Wrench" size={14} />
                          My Garage / My Projects
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(item)}
                  iconName="X"
                  iconPosition="left"
                  className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  Remove
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 sm:p-6 shadow-soft">
      {/* Header with Tabs - Always visible */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 bg-gradient-to-br ${config?.gradient} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon name={config?.icon} size={16} color="white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground truncate">{config?.title}</h2>
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-1">
                {config?.description}
              </p>
            </div>
          </div>
          {currentItems?.length > 0 && !isLoading && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadItems} 
              iconName="RefreshCw"
              className="w-full sm:w-auto"
            >
              Refresh
            </Button>
          )}
        </div>

        {/* Tab Navigation - Responsive with wrap */}
        <div className="border-b border-border overflow-x-auto">
          <nav className="flex gap-4 sm:gap-8 min-w-max">
            <button
              onClick={() => setActiveTab('favorites')}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                activeTab === 'favorites' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <Icon name="Heart" size={16} />
              <span>Favorites</span>
              <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                {favoritesItems?.length || 0}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('savedForLater')}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                activeTab === 'savedForLater' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <Icon name="Bookmark" size={16} />
              <span>Saved for Later</span>
              <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                {savedForLaterItems?.length || 0}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('garage')}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                activeTab === 'garage' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <Icon name="Wrench" size={16} />
              <span>My Garage / My Projects</span>
              <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                {garageItems?.length || 0}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                activeTab === 'requests' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <Icon name="Inbox" size={16} />
              <span>Requests</span>
              <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                {requestItems?.length || 0}
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Notification Toast */}
      {notification?.show && (
        <div className={`mb-4 px-4 py-3 rounded-lg border ${
          notification?.type === 'success' ?'bg-success/10 border-success text-success' :'bg-error/10 border-error text-error'
        }`}>
          <div className="flex items-center gap-2">
            <Icon name={notification?.type === 'success' ? 'Check' : 'AlertCircle'} size={16} />
            <span className="text-sm font-medium break-words">{notification?.message}</span>
          </div>
        </div>
      )}

      {/* Dynamic Content Area */}
      {renderContent()}
    </div>
  );
};

export default WishlistManager;